import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import {
  ButtonProps,
  Button,
  Box,
  TextField,
  Typography,
  Stack
} from "@mui/material"
import {styled} from "@mui/material/styles"
import React from "react"
import {InfoModal} from "components/InfoModal"
import {YearDatePicker} from "components/YearDatePicker"
import {IQuestion} from "models/IQuestion"

interface SelectOneProps {
  question: IQuestion
  answer: string
  year?: string
  subAnswer?: string
  onSelectAnswer: (answer: string, subAnswer?: string, year?: string) => void
}

type AnswerButtonProps = ButtonProps & {
  selectedanswer: string
  anscheck: string
}
const AnswerButton = styled(Button)<AnswerButtonProps>(
  ({selectedanswer, anscheck}) => ({
    borderRadius: "17px",
    height: "70px",
    fontSize: "24px",
    color: "#707070",
    justifyContent: "unset",
    border: `${
      selectedanswer === anscheck ? "4px solid #2663E5" : "1px solid #707070"
    }`,
    ":hover": {
      border: "1px solid #2663E5"
    }
  })
)

export const SelectOne: React.FC<SelectOneProps> = (props: SelectOneProps) => {
  const {question, answer, year = "", subAnswer = "", onSelectAnswer} = props
  const {info, yearOption, reasonOption} = question

  // Whether a follow-up field (year picker / reason text) should show for a
  // given answer. Both option shapes share conditionalShow + conditionalAnswer.
  const shouldShowFollowUp = (
    option: {conditionalShow: boolean; conditionalAnswer: string[]} | undefined,
    a: string
  ): boolean =>
    !!option &&
    !!a &&
    (!option.conditionalShow || option.conditionalAnswer.includes(a))

  const shouldShowYear = (a: string): boolean =>
    shouldShowFollowUp(yearOption, a)
  const shouldShowReason = (a: string): boolean =>
    shouldShowFollowUp(reasonOption, a)

  const onAnswerClicked = (clicked: string) => {
    // Drop year/reason that no longer apply to the newly selected answer.
    onSelectAnswer(
      clicked,
      shouldShowReason(clicked) ? subAnswer : "",
      shouldShowYear(clicked) ? year : ""
    )
  }

  const onYearChanged = (updatedYear: string) => {
    onSelectAnswer(answer, subAnswer, updatedYear)
  }

  const onReasonChanged = (updatedReason: string) => {
    onSelectAnswer(answer, updatedReason, year)
  }

  const getLabel = (label: string) =>
    label.includes("{selectedAnswer}")
      ? label.replace("{selectedAnswer}", answer)
      : label

  return (
    <Box>
      <Box marginBottom="20px">
        <Typography
          fontSize={"28px"}
          fontWeight="600"
          variant="h6"
          gutterBottom
          component="div"
        >
          {question.text}
          {info && <InfoModal content={info.label} />}
        </Typography>
      </Box>
      <Stack spacing={2}>
        {question.answerOptions.map((ans, i) => (
          <AnswerButton
            key={`answerOptions_${i}`}
            selectedanswer={answer}
            anscheck={ans}
            onClick={() => onAnswerClicked(ans)}
            fullWidth
            variant="outlined"
            startIcon={
              <CheckCircleIcon
                sx={{
                  fontSize: "38px !important",
                  color: `${answer === ans ? "#66bb6a" : ""}`
                }}
              />
            }
          >
            {ans}
          </AnswerButton>
        ))}
      </Stack>
      {shouldShowYear(answer) && yearOption && (
        <Box marginTop="20px">
          <Typography
            fontSize={"28px"}
            fontWeight="600"
            variant="h6"
            gutterBottom
            component="div"
          >
            {getLabel(yearOption.label)}
          </Typography>
          <YearDatePicker onChange={onYearChanged} year={year} />
        </Box>
      )}
      {shouldShowReason(answer) && reasonOption && (
        <Box marginTop="20px">
          <Typography
            fontSize={"20px"}
            fontWeight="600"
            variant="h6"
            gutterBottom
            component="div"
          >
            {getLabel(reasonOption.label)}
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            value={subAnswer}
            onChange={(e) => onReasonChanged(e.target.value)}
          />
        </Box>
      )}
    </Box>
  )
}
